import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PAGE_NAMES = {
  '/': 'Dashboard',
  '/book-of-record': 'Book of Record',
  '/ledger': 'Asset Ledger',
  '/nav-pricing': 'NAV & Pricing',
  '/order-crossing': 'Order Crossing',
  '/predictions': 'Predictions',
  '/rebalancing': 'Rebalancing',
  '/activity': 'Activity Log',
};

export function usePageTracker() {
  const { user, trackPageView } = useAuth();
  const location = useLocation();
  const enteredAt = useRef(Date.now());
  const lastPage = useRef(null);

  useEffect(() => {
    if (!user) return;

    const currentPage = PAGE_NAMES[location.pathname] || location.pathname;

    // Send dwell time for previous page
    if (lastPage.current && lastPage.current !== currentPage) {
      const dwell = Math.round((Date.now() - enteredAt.current) / 1000);
      if (dwell > 1) {
        trackPageView(lastPage.current, dwell);
      }
    }

    // Track new page view (0 dwell = just arrived)
    trackPageView(currentPage, 0);
    lastPage.current = currentPage;
    enteredAt.current = Date.now();

    // Also track on page unload
    const handleUnload = () => {
      const dwell = Math.round((Date.now() - enteredAt.current) / 1000);
      if (dwell > 1 && lastPage.current) {
        // Use sendBeacon for reliable unload tracking
        const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
        navigator.sendBeacon(
          `${API_BASE}/api/admin/auth/track`,
          new Blob([JSON.stringify({ page: lastPage.current, dwell_seconds: dwell })], { type: 'application/json' })
        );
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [location.pathname, user, trackPageView]);
}
